// JWT payload helpers.
//
// IMPORTANT: Supabase's `session.user.app_metadata` is sourced from the
// `auth.users.app_metadata` JSONB column (eg `{provider, providers}`).
// It is NOT the same as the JWT's `app_metadata` claim — which is what
// the auth-hook (Custom Access Token Hook) injects custom claims into.
//
// To read custom claims like `workspaces`, decode the access_token JWT
// directly. supabase-js does not surface JWT claims through the User
// object.

export interface JwtClaims {
  sub?: string;
  exp?: number;
  iat?: number;
  email?: string;
  app_metadata?: Record<string, unknown>;
  user_metadata?: Record<string, unknown>;
  [key: string]: unknown;
}

export function decodeJwtPayload(token: string | undefined | null): JwtClaims | null {
  if (!token) return null;
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    // base64url → base64
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    return JSON.parse(atob(padded)) as JwtClaims;
  } catch {
    return null;
  }
}

export interface JwtWorkspaceMembership {
  workspace_id: string;
  role: "owner" | "admin" | "editor" | "viewer";
}

export function readWorkspacesFromToken(
  token: string | undefined | null,
): JwtWorkspaceMembership[] {
  const claims = decodeJwtPayload(token);
  const raw = (claims?.app_metadata as Record<string, unknown> | undefined)
    ?.workspaces;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (w): w is JwtWorkspaceMembership =>
      typeof w === "object" &&
      w !== null &&
      typeof (w as JwtWorkspaceMembership).workspace_id === "string" &&
      typeof (w as JwtWorkspaceMembership).role === "string",
  );
}
